import { forwardRef, useEffect, useState } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseAvatarProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

const sizeClassNames = {
  sm: 'h-7 w-7 text-xs',
  md: 'h-9 w-9 text-sm',
  lg: 'h-12 w-12 text-base',
} as const;

const statusClassNames = {
  online: 'bg-[hsl(var(--success,142_72%_29%))]',
  busy: 'bg-[hsl(var(--warning,45_93%_47%))]',
  offline: 'bg-muted-foreground',
  neutral: 'bg-muted-foreground',
} as const;

function isStatusPreset(status: unknown): status is keyof typeof statusClassNames {
  return typeof status === 'string' && status in statusClassNames;
}

function getInitials(name?: string) {
  if (!name) return undefined;
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return undefined;
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('');
}

export const BaseAvatar = forwardRef<HTMLDivElement, BaseAvatarProps>(
  function BaseAvatar(
    {
      src,
      alt,
      name,
      initials,
      icon,
      status,
      size = 'md',
      appearance,
      className,
      classNames,
      style,
      children,
      title,
      ...avatarProps
    },
    ref,
  ) {
    if (size !== 'sm' && size !== 'md' && size !== 'lg') {
      throw new TypeError('BaseAvatar size must be "sm", "md", or "lg".');
    }

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const [failedSrc, setFailedSrc] = useState<string>();
    const imageFailed = Boolean(src && failedSrc === src);
    const showImage = Boolean(src) && !imageFailed;
    const generatedInitials = getInitials(name);
    const fallbackKind = hasRenderableNode(children)
      ? 'children'
      : hasRenderableNode(initials)
        ? 'initials'
        : hasRenderableNode(generatedInitials)
          ? 'name'
          : hasRenderableNode(icon)
            ? 'icon'
            : 'default';
    const fallback = hasRenderableNode(children)
      ? children
      : hasRenderableNode(initials)
        ? initials
        : hasRenderableNode(generatedInitials)
          ? generatedInitials
          : hasRenderableNode(icon)
            ? icon
            : '?';
    const statusKind = isStatusPreset(status)
      ? status
      : hasRenderableNode(status)
        ? 'custom'
        : undefined;

    useEffect(() => {
      setFailedSrc(undefined);
    }, [src]);

    return (
      <div
        {...componentAttributes}
        {...avatarProps}
        ref={ref}
        title={title ?? name}
        data-monkeys-component="avatar"
        data-slot="root"
        data-state={showImage ? 'image' : 'fallback'}
        data-size={size}
        data-status={statusKind}
        data-fallback={showImage ? undefined : fallbackKind}
        className={cn(
          theme.slots.avatarRoot,
          sizeClassNames[size],
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.avatar,
          ...style,
        }}
      >
        {showImage ? (
          <img
            src={src}
            alt={alt ?? name ?? ''}
            data-slot="image"
            data-size={size}
            data-status={statusKind}
            className={cn(theme.slots.avatarImage, classNames?.image)}
            onError={() => setFailedSrc(src)}
          />
        ) : (
          <span
            data-slot="fallback"
            data-size={size}
            data-status={statusKind}
            data-fallback={fallbackKind}
            className={cn(
              theme.slots.avatarFallback,
              classNames?.fallback,
              classNames?.text,
            )}
          >
            {fallback}
          </span>
        )}

        {isStatusPreset(status) ? (
          <span
            aria-hidden="true"
            data-slot="status"
            data-size={size}
            data-status={status}
            className={cn(
              theme.slots.avatarStatus,
              statusClassNames[status],
              classNames?.status,
            )}
          />
        ) : hasRenderableNode(status) ? (
          <span
            data-slot="status"
            data-size={size}
            data-status="custom"
            className={cn(theme.slots.avatarStatus, classNames?.status)}
          >
            {status}
          </span>
        ) : null}
      </div>
    );
  },
);
